<template>
  <div class="service-page">
    <!-- Carousel Hero Section -->
    <ServicesItsCarouselComp 
      :slides="normalBikeSlides"
      background-image="/resource/portfolio-04.jpg"
      pattern-image="/shapes/shape-06.png"
      :mission-vision="normalBikeMissionVision"
    />

    <!-- Bike Services Showcase Section -->
    <ServiceItsShowcaseCardComp
      :header-data="bikeShowcaseHeaderData"
      :services-data="bikeServicesData"
      button-text="Learn More"
    />

    <!-- Cycling Community Section -->
    <ServiceItsCommunityJoinComp
      :community-data="cyclingCommunityData"
    />

    <!-- Bike Maintenance Tips Section -->
    <ServiceItsSliderRegularComp
      :header-data="maintenanceTipsHeaderData"
      :tips-data="maintenanceTips"
    />
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import WOW from 'wowjs'
import ServicesItsCarouselComp from '@/components/ServicesItsCarouselComp.vue'
import ServiceItsShowcaseCardComp from '@/components/ServiceItsShowcaseCardComp.vue'
import ServiceItsCommunityJoinComp from '@/components/ServiceItsCommunityJoinComp.vue'
import ServiceItsSliderRegularComp from '@/components/ServiceItsSliderRegularComp.vue'

export default {
  name: 'NormalBikeServicesPage',
  components: {
    ServicesItsCarouselComp,
    ServiceItsShowcaseCardComp,
    ServiceItsCommunityJoinComp,
    ServiceItsSliderRegularComp
  },
  setup() {
    const tipsSwiper = ref(null)
    
    const bikeServicesData = ref([
      {
        icon: 'fas fa-cog',
        title: 'Brake Services',
        description: 'Complete brake system maintenance and repair for both rim and disc brakes to ensure safe riding.',
        features: ['Brake Pad Replacement', 'Cable Adjustment', 'Disc Brake Service', 'Rim Brake Tuning'],
        detailedDescription: 'Professional brake system service with quality parts and expert installation.'
      },
      {
        icon: 'fas fa-cogs',
        title: 'Gear Services',
        description: 'Professional gear system maintenance and adjustment for smooth shifting performance.',
        features: ['Derailleur Adjustment', 'Gear Cable Replacement', 'Cassette Service', 'Chain Replacement'],
        detailedDescription: 'Precision gear tuning for optimal shifting performance and durability.'
      },
      {
        icon: 'fas fa-circle',
        title: 'Wheel Services',
        description: 'Complete wheel maintenance including truing, spoke replacement, and bearing service.',
        features: ['Wheel Truing', 'Spoke Replacement', 'Bearing Service', 'Hub Overhaul'],
        detailedDescription: 'Expert wheel maintenance for smooth rolling and long-lasting performance.'
      },
      {
        icon: 'fas fa-tire',
        title: 'Tire Services',
        description: 'Professional tire maintenance including puncture repair, tube replacement, and tire fitting.',
        features: ['Puncture Repair', 'Tube Replacement', 'Tire Fitting', 'Pressure Check'],
        detailedDescription: 'Complete tire and tube services for all riding conditions and terrains.'
      },
      {
        icon: 'fas fa-bicycle',
        title: 'Frame Services',
        description: 'Complete frame maintenance including cleaning, inspection, and minor repairs.',
        features: ['Frame Cleaning', 'Paint Touch-up', 'Frame Inspection', 'Component Mounting'],
        detailedDescription: 'Professional frame care to maintain your bike\'s appearance and structural integrity.'
      },
      {
        icon: 'fas fa-wrench',
        title: 'Complete Tune-up',
        description: 'Comprehensive bike tune-up service to ensure optimal performance and safety.',
        features: ['Full Inspection', 'Lubrication Service', 'Component Adjustment', 'Safety Check'],
        detailedDescription: 'Complete bike overhaul for peak performance and maximum safety.'
      }
    ])

    const maintenanceTips = ref([
      {
        image: require('@/assets/resource/portfolio-02.jpg'),
        title: 'Regular Chain Lubrication',
        description: 'Keep your chain properly lubricated to ensure smooth shifting and prevent wear.',
        tags: ['Maintenance', 'Chain Care', 'Performance']
      },
      {
        image: require('@/assets/resource/portfolio-03.jpg'),
        title: 'Tire Pressure Check',
        description: 'Check tire pressure weekly for optimal performance and safety on all terrains.',
        tags: ['Safety', 'Tire Care', 'Performance']
      },
      {
        image: require('@/assets/resource/portfolio-04.jpg'),
        title: 'Brake Pad Inspection',
        description: 'Regularly inspect brake pads for wear and replace when necessary for safety.',
        tags: ['Safety', 'Brake Care', 'Inspection']
      },
      {
        image: require('@/assets/resource/portfolio-01.jpg'),
        title: 'Gear Adjustment',
        description: 'Fine-tune your gears for smooth shifting and optimal performance.',
        tags: ['Gears', 'Adjustment', 'Performance']
      }
    ])

    const initTipsSwiper = () => {
      setTimeout(() => {
        const checkSwiper = () => {
          if (window.Swiper) {
            tipsSwiper.value = new window.Swiper('.tips-slider', {
              loop: true,
              autoplay: {
                delay: 3000,
                disableOnInteraction: false,
              },
              pagination: {
                el: '.swiper-pagination',
                clickable: true,
              },
              breakpoints: {
                0: { slidesPerView: 1, spaceBetween: 20 },
                768: { slidesPerView: 2, spaceBetween: 30 },
                1024: { slidesPerView: 3, spaceBetween: 30 }
              }
            });
          } else {
            setTimeout(checkSwiper, 100);
          }
        };
        checkSwiper();
      }, 500);
    }

    onMounted(async () => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false
      }).init()

      // Import and initialize Owl Carousel
      try {
        await import('owl.carousel')
        
        // Initialize Owl Carousel for banner
        setTimeout(() => {
          if (window.$ && window.$.fn.owlCarousel) {
            window.$('.banner-carousel').owlCarousel({
              loop: true,
              margin: 0,
              nav: true,
              active: true,
              smartSpeed: 1000,
              autoplay: 6000,
              navText: ['<span class="icon-05"></span>', '<span class="icon-06"></span>'],
              responsive: {
                0: {
                  items: 1
                },
                600: {
                  items: 1
                },
                768: {
                  items: 1
                },
                992: {
                  items: 1
                },
                1200: {
                  items: 1
                }
              }
            })
          }
        }, 100)
      } catch (error) {
        console.error('Error loading Owl Carousel:', error)
      }
      initTipsSwiper()
    })

    // Normal Bike Services slides data
    const normalBikeSlides = ref([
      {
        subtitle: 'Normal Bike Services',
        titleLines: [
          { text: 'Professional ', highlight: false },
          { text: 'Bicycle ', highlight: 'Care' }
        ],
        description: 'Comprehensive bicycle services for all types of bikes. From routine maintenance to complex repairs, we provide professional bike care solutions.',
        buttonText: 'Get Service',
        buttonLink: '/contact'
      },
      {
        subtitle: 'Bike Specialists',
        titleLines: [
          { text: 'Expert ', highlight: 'Mechanics' }
        ],
        description: 'Our certified bike mechanics use professional tools and genuine parts to ensure your bicycle receives the best possible care and maintenance.',
        buttonText: 'Learn More',
        buttonLink: '/contact'
      },
      {
        subtitle: 'Quality Service',
        titleLines: [
          { text: 'Reliable ', highlight: 'Solutions' }
        ],
        description: 'We offer a wide range of bicycle services including brake repair, gear adjustment, wheel service, and preventive maintenance to keep your bike running smoothly.',
        buttonText: 'Book Now',
        buttonLink: '/contact'
      }
    ]);

    // Normal Bike Services mission/vision data
    const normalBikeMissionVision = ref({
      vision: {
        title: 'Our Vision',
        description: 'To be the leading bicycle service provider offering comprehensive solutions for all bike types with unmatched quality and customer satisfaction.'
      },
      mission: {
        title: 'Our Mission',
        description: 'To deliver exceptional bicycle services using advanced tools, skilled mechanics, and quality parts to ensure optimal bike performance and safety.'
      }
    });

    // Bike Services Showcase header data
    const bikeShowcaseHeaderData = {
      icon: "fas fa-bicycle",
      tag: "OUR BIKE SERVICES",
      title: "Bicycle Care Excellence",
      description: "Professional bicycle services with precision and passion for cycling"
    }

    // Cycling Community data
    const cyclingCommunityData = {
      title: "Join Our Cycling Community",
      description: "Connect with fellow cyclists, share experiences, and discover new biking adventures in our vibrant community.",
      stats: [
        { number: "500+", label: "Active Members" },
        { number: "50+", label: "Group Rides" },
        { number: "1000+", label: "Bikes Serviced" }
      ],
      buttonText: "Join Community",
      image: require("@/assets/resource/portfolio-01.jpg"),
      imageAlt: "Cycling Community"
    }

    // Maintenance Tips header data
    const maintenanceTipsHeaderData = {
      icon: "fas fa-tools",
      tag: "MAINTENANCE TIPS",
      title: "Keep Your Bike in Top Condition",
      description: "Expert tips and tricks for maintaining your bicycle"
    }

    return {
      bikeServicesData,
      maintenanceTips,
      tipsSwiper,
      normalBikeSlides,
      normalBikeMissionVision,
      bikeShowcaseHeaderData,
      cyclingCommunityData,
      maintenanceTipsHeaderData
    }
  }
}
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Custom button styling for carousel - More specific to avoid conflicts */
.service-page .banner__two .btn-box .theme-btn {
  padding: 12px 30px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  border-radius: 0 !important;
  background: linear-gradient(219deg, #FB0E76 0%, #910694 100%) !important;
  color: #fff !important;
  text-decoration: none !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 8px !important;
  transition: all 0.3s ease !important;
}

.service-page .banner__two .btn-box .theme-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 5px 15px rgba(251, 14, 118, 0.4) !important;
  color: #fff !important;
  text-decoration: none !important;
}

.service-page .banner__two .btn-box .theme-btn i {
  font-size: 12px !important;
  transition: transform 0.3s ease !important;
}

.service-page .banner__two .btn-box .theme-btn:hover i {
  transform: translateX(3px) !important;
}

/* Mission/Vision block styling */
.banner__mission__block {
  border-radius: 10px 0px 0px 0px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(15px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  margin-top: 20px;
}

.mission__block {
  padding: 40px 50px;
  border-right: 1px solid rgba(255, 255, 255, 0.10);
  flex: 1;
}

.mission__block:last-child {
  border-right: none;
}

.mission__block h3 {
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.mission__block p {
  color: #ccc;
  font-size: 14px;
  line-height: 24px;
  margin: 0;
  font-family: 'Open Sans', sans-serif;
}

/* Reduce banner height - More specific to avoid conflicts */
.service-page section.banner__two {
  padding-top: 100px !important;
  padding-bottom: 100px !important;
}

.service-page .banner__two .content-box {
  padding-bottom: 60px !important;
}

/* Responsive adjustments - More specific to avoid conflicts */
@media (max-width: 768px) {
  .service-page .banner__two .content-box h2 {
    font-size: 50px !important;
    line-height: 60px !important;
  }
  
  .service-page .mission__block {
    padding: 30px 25px !important;
  }
  
  .service-page .mission__block h3 {
    font-size: 18px !important;
  }
  
  .service-page .mission__block p {
    font-size: 13px !important;
    line-height: 22px !important;
  }
}

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* Global Styles */
body {
  font-family: 'Open Sans', sans-serif;
  line-height: 1.6;
  color: #333;
}

.see__pad {
  padding: 100px 0;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

/* Pattern Layers */
.pattern-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.pattern-1 {
  top: 10%;
  right: 5%;
  width: 100px;
  height: 100px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-2 {
  bottom: 20%;
  left: 8%;
  width: 80px;
  height: 80px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-3 {
  top: 30%;
  left: 10%;
  width: 120px;
  height: 120px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-4 {
  top: 15%;
  right: 15%;
  width: 90px;
  height: 90px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-5 {
  bottom: 25%;
  right: 20%;
  width: 70px;
  height: 70px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-6 {
  top: 40%;
  left: 5%;
  width: 110px;
  height: 110px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.09;
}

.pattern-7 {
  bottom: 15%;
  left: 12%;
  width: 85px;
  height: 85px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-8 {
  top: 35%;
  right: 8%;
  width: 95px;
  height: 95px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

/* Intro Section */
.service-intro {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.service-intro-content {
  position: relative;
  z-index: 2;
}

.service-section-label h4 {
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 13.2px;
  color: #F6EC4F;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
  text-transform: uppercase;
}

.service-main-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.service-description p {
  font-size: 18px;
  color: #fff;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
}

/* Services Offered Section */
.services-offered {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.services-header {
  position: relative;
  z-index: 2;
  margin-bottom: 60px;
}

.services-section-label h4 {
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 13.2px;
  color: #F6EC4F;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
  text-transform: uppercase;
}

.services-main-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.services-description p {
  font-size: 18px;
  color: #fff;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
}

.service-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(246, 236, 79, 0.1);
  border-radius: 15px;
  padding: 40px 30px;
  height: 100%;
  position: relative;
  transition: all 0.3s ease;
  overflow: hidden;
}

.service-item:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 50px rgba(246, 236, 79, 0.2);
  border-color: #F6EC4F;
}

.service-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.block-shape-1 {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, rgba(246, 236, 79, 0.1), rgba(246, 236, 79, 0.05));
  border-radius: 50%;
  opacity: 0.3;
}

.block-shape-2 {
  position: absolute;
  bottom: -30px;
  left: -30px;
  width: 80px;
  height: 80px;
  background: linear-gradient(45deg, rgba(246, 236, 79, 0.08), rgba(246, 236, 79, 0.03));
  border-radius: 50%;
  opacity: 0.4;
}

.service-content {
  position: relative;
  z-index: 2;
}

.service-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #F6EC4F, #E6D43A);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
}

.service-icon i {
  font-size: 24px;
  color: #00061A;
}

.service-content h3 {
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.service-content p {
  font-size: 16px;
  color: #fff;
  margin-bottom: 20px;
  line-height: 1.6;
}

.service-content ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.service-content ul li {
  color: #fff;
  font-size: 14px;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.service-content ul li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #F6EC4F;
  font-weight: bold;
}

/* Call to Action Section */
.service-cta {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.cta-content {
  position: relative;
  z-index: 2;
}

.cta-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.cta-description p {
  font-size: 18px;
  color: #fff;
  max-width: 600px;
  margin: 0 auto 40px;
  line-height: 1.8;
}

.cta-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.primary-btn {
  background: linear-gradient(135deg, #F6EC4F, #E6D43A);
  color: #00061A;
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(246, 236, 79, 0.3);
}

.secondary-btn {
  background: transparent;
  color: #F6EC4F;
  border-color: #F6EC4F;
}

.secondary-btn:hover {
  background: #F6EC4F;
  color: #00061A;
  transform: translateY(-2px);
}

/* Responsive Design */
@media (max-width: 767px) {
  .service-main-title h2,
  .services-main-title h2,
  .cta-title h2 {
    font-size: 36px;
    line-height: 46px;
  }
  
  .service-section-label h4,
  .services-section-label h4 {
    letter-spacing: 8px;
    font-size: 16px;
  }
  
  .service-item {
    padding: 30px 20px;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .cta-btn {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
}

@media (max-width: 399px) {
  .see__pad {
    padding: 60px 0;
  }
  
  .service-main-title h2,
  .services-main-title h2,
  .cta-title h2 {
    font-size: 28px;
    line-height: 38px;
  }
}

/* Bike Services Showcase Section - Now handled by ServiceItsShowcaseCardComp component */

/* Cycling Community Section - Now handled by ServiceItsCommunityJoinComp component */

/* Maintenance Tips Section - Now handled by ServiceItsSliderRegularComp component */

/* Responsive Design for New Sections - Now handled by components */
</style> 